import React, { useEffect, useState, useMemo } from "react"
import { useParams } from "react-router-dom";
import config from '../config.js';
import { apiErrorMessage } from '../utils/apiError';
import TaxCertificatesCard from '../components/TaxCertificatesCard';
import { desktopUi } from '../features/desktopUi';
import {
  loadCustomers as fetchCustomers, filterCustomers, loadCustomer, loadContacts as fetchContacts,
  loadAddresses as fetchAddresses, taxRateDisplay, contactForm, saveContact, addressForm, saveAddress,
} from '../features/customers/customerData';

const API_BASE = config.API_BASE_URL;

export default function CustomerManagementPage() {
  // /customers/:customerId deep link (Sales Tax Report "Customer" button, the
  // office Slack ping on a portal upload) — select that customer once the
  // list has loaded. Plain /customers leaves the page on "new customer".
  const { customerId: routeCustomerId } = useParams();
  const [deepLinked, setDeepLinked] = useState(null)
  // ===================== Left side: Customer List + Search =====================
  const [allCustomers, setAllCustomers] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loadingList, setLoadingList] = useState(false)
  const [listError, setListError] = useState(null)

  // ===================== Right side: Customer Form (New or Edit) =====================
  const [selectedCustomerId, setSelectedCustomerId] = useState(null) // null => new
  const [loadingForm, setLoadingForm] = useState(false)
  const [formError, setFormError] = useState(null)

  // Main Customer Fields:
  const [name, setName] = useState("")
  const [contactName, setContactName] = useState("") // Legacy 'Main' contact
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [address, setAddress] = useState("")
  const [city, setCity] = useState("")
  const [stateVal, setStateVal] = useState("")
  const [zipCode, setZipCode] = useState("")
  const [notes, setNotes] = useState("")
  const [billComId, setBillComId] = useState("")
  // Store as percentage string in UI (e.g. "8.25"), convert to decimal for API (0.0825)
  const [taxRatePercent, setTaxRatePercent] = useState("0")
  const [taxExempt, setTaxExempt] = useState(false)
  const [paymentTerms, setPaymentTerms] = useState("")
  const [defaultFulfillmentType, setDefaultFulfillmentType] = useState("shipping")
  const [shortCode, setShortCode] = useState("")

  // ===================== Sub-Section: Contacts List =====================
  const [contacts, setContacts] = useState([])
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  
  // Contact Form State (for the modal)
  const [contactId, setContactId] = useState(null) // null => new contact
  const [cName, setCName] = useState("")
  const [cEmail, setCEmail] = useState("")
  const [cPhone, setCPhone] = useState("")
  const [cRole, setCRole] = useState("General")

  // ===================== Sub-Section: Ship-To Addresses =====================
  const [addresses, setAddresses] = useState([])
  const [loadingAddresses, setLoadingAddresses] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)

  // Address Form State (for the modal)
  const [addressId, setAddressId] = useState(null) // null => new address
  const [aLabel, setALabel] = useState("")
  const [aAddress, setAAddress] = useState("")
  const [aAddress2, setAAddress2] = useState("")
  const [aCity, setACity] = useState("")
  const [aState, setAState] = useState("")
  const [aZip, setAZip] = useState("")
  const [aContactName, setAContactName] = useState("")
  const [aContactPhone, setAContactPhone] = useState("")
  const [aNotes, setANotes] = useState("")
  const [aReceivingHours, setAReceivingHours] = useState("")
  const [aIsDefault, setAIsDefault] = useState(false)

  // ===================== On mount, load entire customer list =====================
  useEffect(() => {
    loadCustomersList()
  }, [])

  async function loadCustomersList() {
    setLoadingList(true)
    setListError(null)
    try {
      setAllCustomers(await fetchCustomers()) // features/customers/customerData: by name
    } catch (err) {
      console.error(err)
      setListError(err.message)
    } finally {
      setLoadingList(false)
    }
  }

  // ===================== Filter customers =====================
  const filteredCustomers = filterCustomers(allCustomers, searchTerm) // customerData

  // ===================== Selection Logic =====================
  // Deep link: once the list is in, select the routed customer (once per id).
  useEffect(() => {
    const id = Number(routeCustomerId)
    if (!id || deepLinked === id || allCustomers.length === 0) return
    if (allCustomers.some(c => c.customer_id === id)) {
      setDeepLinked(id)
      handleSelectCustomer(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCustomerId, allCustomers])

  async function handleSelectCustomer(customerId) {
    setSelectedCustomerId(customerId)
    setLoadingForm(true)
    setFormError(null)
    setContacts([]) // Clear old contacts while loading
    setAddresses([])

    try {
      // 1. Load Main Customer Data
      const data = await loadCustomer(customerId) // features/customers/customerData
      
      setName(data.name || "")
      setContactName(data.contact_name || "")
      setPhone(data.phone || "")
      setEmail(data.email || "")
      setAddress(data.address || "")
      setCity(data.city || "")
      setStateVal(data.state || "")
      setZipCode(data.zip_code || "")
      setNotes(data.notes || "")
      setBillComId(data.bill_com_id || "")
      setShortCode(data.short_code || "")

      // Convert decimal (0.085) to percent string ("8.5") for display (customerData)
      const tax = taxRateDisplay(data.tax_rate)
      setTaxExempt(tax.exempt)
      setTaxRatePercent(tax.percent)
      setPaymentTerms(data.payment_terms || "")
      setDefaultFulfillmentType(data.default_fulfillment_type || "shipping")

      // 2. Load Associated Contacts + ship-to addresses
      await loadContacts(customerId)
      await loadAddresses(customerId)

    } catch (err) {
      console.error(err)
      setFormError(err.message)
    } finally {
      setLoadingForm(false)
    }
  }

  // customerData: null when the read fails (the table keeps what it had).
  async function loadContacts(customerId) {
    setLoadingContacts(true)
    const data = await fetchContacts(customerId)
    if (data) setContacts(data)
    setLoadingContacts(false)
  }

  async function loadAddresses(customerId) {
    setLoadingAddresses(true)
    const data = await fetchAddresses(customerId)
    if (data) setAddresses(data)
    setLoadingAddresses(false)
  }

  function handleCreateNew() {
    setSelectedCustomerId(null)
    setFormError(null)
    setLoadingForm(false)
    setContacts([])
    setAddresses([])

    setName("")
    setContactName("")
    setPhone("")
    setEmail("")
    setAddress("")
    setCity("")
    setStateVal("")
    setZipCode("")
    setNotes("")
    setBillComId("")
    setShortCode("")
    setTaxRatePercent("0")
    setTaxExempt(false)
    setPaymentTerms("")
    setDefaultFulfillmentType("shipping")
  }

  // ===================== Main Customer Save =====================
  async function handleSaveCustomer() {
    setLoadingForm(true)
    setFormError(null)

    const missing = [];
    if (!name.trim()) missing.push("Company Name");
    if (!contactName.trim()) missing.push("Main Contact");
    if (!phone.trim()) missing.push("Phone");
    if (!address.trim()) missing.push("Address");
    if (!city.trim()) missing.push("City");
    if (!stateVal.trim()) missing.push("State");
    if (!zipCode.trim()) missing.push("Zip Code");
    if (!email.trim()) missing.push("Email");
    if (!taxExempt && (!taxRatePercent || parseFloat(taxRatePercent) <= 0)) missing.push("Tax Rate");
    if (!paymentTerms) missing.push("Default Payment Terms");

    if (missing.length > 0) {
      setFormError(`Required fields missing: ${missing.join(", ")}`)
      setLoadingForm(false)
      return
    }

    // Convert UI percentage ("8.5") back to decimal (0.085)
    let taxDecimal = 0
    if (!taxExempt) {
      taxDecimal = parseFloat(taxRatePercent)
      if (isNaN(taxDecimal)) taxDecimal = 0
      taxDecimal = taxDecimal / 100
    }

    const payload = {
      name: name.trim(),
      short_code: shortCode.trim() || null,
      contact_name: contactName.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      address: address.trim() || null,
      city: city.trim() || null,
      state: stateVal.trim() || null,
      zip_code: zipCode.trim() || null,
      notes: notes.trim() || null,
      bill_com_id: billComId.trim() || null,
      tax_rate: taxDecimal,
      payment_terms: paymentTerms || null,
      default_fulfillment_type: defaultFulfillmentType || 'shipping'
    }

    try {
      let url = `${API_BASE}/customers`
      let method = "POST"

      if (selectedCustomerId) {
        url = `${API_BASE}/customers/${selectedCustomerId}`
        method = "PATCH"
      }

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
      
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const saved = await res.json()
      
      alert(`Customer saved successfully!`)
      await loadCustomersList()
      
      // If creating new, switch to edit mode so they can add contacts immediately
      if (!selectedCustomerId) {
        setSelectedCustomerId(saved.customer_id)
      }

    } catch (err) {
      console.error(err)
      setFormError(err.message)
    } finally {
      setLoadingForm(false)
    }
  }

  // ===================== Contact Modal Logic =====================
  function openContactModal(contact = null) {
    const f = contactForm(contact) // customerData: a new one is General
    setContactId(contact ? contact.contact_id : null)
    setCName(f.name)
    setCEmail(f.email)
    setCPhone(f.phone)
    setCRole(f.role)
    setShowContactModal(true)
  }

  async function handleSaveContact() {
    // customerData (the phone's too): the name check, POST / PATCH, the refusal alert.
    const r = await saveContact({
      customerId: selectedCustomerId, contactId, form: { name: cName, email: cEmail, phone: cPhone, role: cRole }, ui: desktopUi,
    })
    if (!r.done) return
    // Refresh contacts list
    await loadContacts(selectedCustomerId)
    setShowContactModal(false)
  }

  // ===================== Ship-To Address Modal Logic =====================
  function openAddressModal(addr = null) {
    const f = addressForm(addr, { isFirst: addresses.length === 0 }) // customerData: the first address defaults
    setAddressId(addr ? addr.address_id : null)
    setALabel(f.label)
    setAAddress(f.address)
    setAAddress2(f.address_line2)
    setACity(f.city)
    setAState(f.state)
    setAZip(f.zip_code)
    setAContactName(f.contact_name)
    setAContactPhone(f.contact_phone)
    setANotes(f.delivery_notes)
    setAReceivingHours(f.receiving_hours)
    setAIsDefault(f.is_default)
    setShowAddressModal(true)
  }

  async function handleSaveAddress() {
    // customerData (the phone's too): the label check, the trimmed body, POST / PATCH, the API's refusal.
    const r = await saveAddress({
      customerId: selectedCustomerId,
      addressId,
      form: {
        label: aLabel, address: aAddress, address_line2: aAddress2, city: aCity, state: aState, zip_code: aZip,
        contact_name: aContactName, contact_phone: aContactPhone, delivery_notes: aNotes,
        receiving_hours: aReceivingHours, is_default: aIsDefault,
      },
      ui: desktopUi,
    })
    if (!r.done) return
    await loadAddresses(selectedCustomerId)
    setShowAddressModal(false)
  }

  async function handleDeleteAddress(addr) {
    if (!window.confirm(`Remove ship-to "${addr.label}"? Sales orders already pointing at it keep it on their documents.`)) return
    try {
      const res = await fetch(`${API_BASE}/addresses/${addr.address_id}`, { method: "DELETE" })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await loadAddresses(selectedCustomerId)
    } catch (err) {
      alert(err.message)
    }
  }

  // ===================== New Customer Checklist =====================
  const isNewCustomer = !selectedCustomerId;

  const newCustomerChecklist = useMemo(() => {
    const items = [
      { id: 'company-name', label: 'Company Name', section: 'customer-form', complete: !!name.trim(), required: true },
      { id: 'main-contact', label: 'Main Contact', section: 'customer-form', complete: !!contactName.trim(), required: true },
      { id: 'phone', label: 'Phone', section: 'customer-form', complete: !!phone.trim(), required: true },
      { id: 'address', label: 'Address', section: 'customer-form', complete: !!address.trim(), required: true },
      { id: 'city', label: 'City', section: 'customer-form', complete: !!city.trim(), required: true },
      { id: 'state', label: 'State', section: 'customer-form', complete: !!stateVal.trim(), required: true },
      { id: 'zip-code', label: 'Zip Code', section: 'customer-form', complete: !!zipCode.trim(), required: true },
      { id: 'email', label: 'Email', section: 'customer-form', complete: !!email.trim(), required: true },
      { id: 'tax-rate', label: 'Tax Rate', section: 'customer-form', complete: taxExempt || (!!taxRatePercent && parseFloat(taxRatePercent) > 0), required: true },
      { id: 'payment-terms', label: 'Default Payment Terms', section: 'customer-form', complete: !!paymentTerms, required: true },
    ];

    const requiredItems = items.filter(i => i.required);
    const completedRequired = requiredItems.filter(i => i.complete).length;
    const totalRequired = requiredItems.length;
    const allRequiredComplete = completedRequired === totalRequired;
    const progressPercent = totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0;

    return { items, completedRequired, totalRequired, allRequiredComplete, progressPercent };
  }, [name, contactName, phone, address, city, stateVal, zipCode, email, taxRatePercent, taxExempt, paymentTerms]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="container">
      <h4>Customers Management</h4>
      {listError && <div className="alert alert-danger">{listError}</div>}

      <div className="row" style={{ marginTop: "1rem" }}>
        {/* LEFT: List */}
        <div className="col-4" style={{ borderRight: "1px solid #ccc", minHeight:"80vh" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <button className="btn btn-sm btn-success" onClick={handleCreateNew}>Create New</button>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "60%" }}
            />
          </div>

          {loadingList ? <p>Loading...</p> : (
            <div className="list-group">
              {filteredCustomers.map((cust) => (
                <button
                  key={cust.customer_id}
                  className={"list-group-item list-group-item-action " + (cust.customer_id === selectedCustomerId ? "active" : "")}
                  onClick={() => handleSelectCustomer(cust.customer_id)}
                >
                  <div className="d-flex w-100 justify-content-between">
                    <h6 className="mb-1">{cust.name}</h6>
                    {cust.short_code && <span className="badge bg-secondary">{cust.short_code}</span>}
                  </div>
                  <small>{cust.city}, {cust.state}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Form */}
        <div className={isNewCustomer ? "col-5" : "col-8"}>
          {loadingForm ? <p>Loading...</p> : (
            <div id="customer-form" style={{ paddingLeft: "1rem" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>{selectedCustomerId ? `Editing Customer #${selectedCustomerId}` : "New Customer"}</h5>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCustomer}
                  disabled={loadingForm}
                >
                  Save Main Details
                </button>
              </div>

              {formError && <div className="alert alert-danger">{formError}</div>}

              {/* Main Fields Grid */}
              <div className="row g-3">
                <div className="col-md-4">
                  <label className={`form-label${!name.trim() ? ' required-field-label' : ''}`}>Company Name</label>
                  <input type="text" className={`form-control${!name.trim() ? ' required-field' : ''}`} value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Short Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={shortCode}
                    onChange={e => setShortCode(e.target.value.toUpperCase())}
                    maxLength={5}
                    placeholder="Auto"
                    style={{ textTransform: "uppercase" }}
                  />
                  <div className="form-text">Appended to material codes (e.g. BIC). Auto-generated if left blank.</div>
                </div>
                <div className="col-md-6">
                  <label className={`form-label${!contactName.trim() ? ' required-field-label' : ''}`}>Main Contact</label>
                  <input type="text" className={`form-control${!contactName.trim() ? ' required-field' : ''}`} value={contactName} onChange={e => setContactName(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className={`form-label${!phone.trim() ? ' required-field-label' : ''}`}>Phone</label>
                  <input type="text" className={`form-control${!phone.trim() ? ' required-field' : ''}`} value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className={`form-label${!email.trim() ? ' required-field-label' : ''}`}>Email</label>
                  <input type="email" className={`form-control${!email.trim() ? ' required-field' : ''}`} value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className={`form-label${!address.trim() ? ' required-field-label' : ''}`}>Address</label>
                  <input type="text" className={`form-control${!address.trim() ? ' required-field' : ''}`} value={address} onChange={e => setAddress(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className={`form-label${!city.trim() ? ' required-field-label' : ''}`}>City</label>
                  <input type="text" className={`form-control${!city.trim() ? ' required-field' : ''}`} value={city} onChange={e => setCity(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className={`form-label${!stateVal.trim() ? ' required-field-label' : ''}`}>State</label>
                  <input type="text" className={`form-control${!stateVal.trim() ? ' required-field' : ''}`} value={stateVal} onChange={e => setStateVal(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className={`form-label${!zipCode.trim() ? ' required-field-label' : ''}`}>Zip Code</label>
                  <input type="text" className={`form-control${!zipCode.trim() ? ' required-field' : ''}`} value={zipCode} onChange={e => setZipCode(e.target.value)} />
                </div>

                <div className="col-md-4">
                  <label className={`form-label${!taxExempt && (!taxRatePercent || parseFloat(taxRatePercent) <= 0) ? ' required-field-label' : ''}`}>Tax Rate (%)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control${!taxExempt && (!taxRatePercent || parseFloat(taxRatePercent) <= 0) ? ' required-field' : ''}`}
                      value={taxExempt ? "0" : taxRatePercent}
                      onChange={e => setTaxRatePercent(e.target.value)}
                      disabled={taxExempt}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-check mt-1">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="taxExemptCheck"
                      checked={taxExempt}
                      onChange={e => {
                        setTaxExempt(e.target.checked)
                        if (e.target.checked) setTaxRatePercent("0")
                      }}
                    />
                    <label className="form-check-label" htmlFor="taxExemptCheck">Tax Exempt</label>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className={`form-label${!paymentTerms ? ' required-field-label' : ''}`}>Default Payment Terms</label>
                  <select className={`form-select${!paymentTerms ? ' required-field' : ''}`} value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}>
                    <option value="">(None)</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 7">Net 7</option>
                    <option value="Net 10">Net 10</option>
                    <option value="Net 14">Net 14</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                  <div className="form-text">Auto-populates on new invoices</div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Default Fulfillment <span className="text-danger">*</span></label>
                  <select className="form-select" value={defaultFulfillmentType || ''} onChange={e => setDefaultFulfillmentType(e.target.value)}>
                    <option value="shipping">Carrier Shipment</option>
                    <option value="pickup">Customer Pickup</option>
                    <option value="delivery">We Deliver</option>
                  </select>
                  <div className="form-text">Required. Auto-populates on new quotes and sales orders for this customer. Pickup orders use the Mark Ready for Pickup → Confirm Pickup workflow.</div>
                </div>

                <div className="col-md-12">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              </div>

              <hr className="my-4" />

              {/* Sales tax exemption certificates (Kansas ST-28D / ST-28A / ST-28M) */}
              {selectedCustomerId ? (
                <TaxCertificatesCard
                  customerId={selectedCustomerId}
                  customer={{ name, contact_name: contactName, email, state: stateVal }}
                  contacts={contacts}
                />
              ) : (
                <>
                  <h5 className="mb-2">Sales Tax Exemption</h5>
                  <div className="alert alert-info">Save the new customer to request or record an exemption certificate.</div>
                </>
              )}

              <hr className="my-4" />

              {/* Contacts Section */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Additional Contacts</h5>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openContactModal(null)}
                  disabled={!selectedCustomerId} // Must save customer first
                >
                  + Add Contact
                </button>
              </div>

              {!selectedCustomerId && (
                <div className="alert alert-info">Save the new customer to add specific contacts.</div>
              )}

              {selectedCustomerId && (
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th style={{width: "80px"}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.length === 0 && (
                        <tr><td colSpan="5" className="text-center text-muted">No contacts added yet.</td></tr>
                      )}
                      {contacts.map(c => (
                        <tr key={c.contact_id}>
                          <td>{c.name}</td>
                          <td><span className="badge bg-secondary">{c.role}</span></td>
                          <td>{c.email}</td>
                          <td>{c.phone}</td>
                          <td>
                            <button 
                              className="btn btn-sm btn-link p-0"
                              onClick={() => openContactModal(c)}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <hr className="my-4" />

              {/* Ship-To Addresses Section */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Ship-To Addresses</h5>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openAddressModal(null)}
                  disabled={!selectedCustomerId}
                >
                  + Add Address
                </button>
              </div>
              <div className="form-text mb-2">
                Delivery locations selectable on this customer's quotes &amp; sales orders. The
                default is pre-selected; the company address above stays the billing address.
              </div>

              {!selectedCustomerId && (
                <div className="alert alert-info">Save the new customer to add ship-to addresses.</div>
              )}

              {selectedCustomerId && (
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-sm">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Address</th>
                        <th>Receiving Contact</th>
                        <th>Receiving Hours</th>
                        <th>Notes</th>
                        <th style={{ width: "120px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {addresses.length === 0 && (
                        <tr><td colSpan="6" className="text-center text-muted">No ship-to addresses yet.</td></tr>
                      )}
                      {addresses.map(a => (
                        <tr key={a.address_id}>
                          <td>
                            {a.label}
                            {a.is_default && <span className="badge bg-success ms-1">Default</span>}
                          </td>
                          <td>
                            <small>
                              {[a.address, a.address_line2].filter(Boolean).join(", ")}
                              {(a.city || a.state || a.zip_code) && <br />}
                              {[a.city, a.state].filter(Boolean).join(", ")} {a.zip_code}
                            </small>
                          </td>
                          <td><small>{a.contact_name}{a.contact_phone ? ` · ${a.contact_phone}` : ""}</small></td>
                          <td><small>{a.receiving_hours}</small></td>
                          <td><small className="text-muted">{a.delivery_notes}</small></td>
                          <td>
                            <button className="btn btn-sm btn-link p-0 me-2" onClick={() => openAddressModal(a)}>Edit</button>
                            <button className="btn btn-sm btn-link p-0 text-danger" onClick={() => handleDeleteAddress(a)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR: New Customer Checklist */}
        {isNewCustomer && (
          <div className="col-3">
            <div className="card border-primary" style={{ position: 'sticky', top: '1rem', zIndex: 100 }}>
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <span><i className="bi bi-list-check me-2"></i>New Customer Checklist</span>
                <span className="badge bg-white text-primary">{newCustomerChecklist.completedRequired}/{newCustomerChecklist.totalRequired}</span>
              </div>
              <div className="card-body p-0">
                {/* Progress Bar */}
                <div className="progress rounded-0" style={{ height: '8px' }}>
                  <div
                    className={`progress-bar ${newCustomerChecklist.allRequiredComplete ? 'bg-success' : 'bg-primary'}`}
                    role="progressbar"
                    style={{ width: `${newCustomerChecklist.progressPercent}%` }}
                    aria-valuenow={newCustomerChecklist.progressPercent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>

                {/* Checklist Items */}
                <ul className="list-group list-group-flush">
                  {newCustomerChecklist.items.map((item) => (
                    <li
                      key={item.id}
                      className={`list-group-item d-flex align-items-center py-2 ${item.complete ? 'bg-success-subtle' : item.required ? 'bg-light' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => scrollToSection(item.section)}
                    >
                      <span className="me-2" style={{ width: '20px' }}>
                        {item.complete ? (
                          <i className="bi bi-check-circle-fill text-success"></i>
                        ) : item.required ? (
                          <i className="bi bi-circle text-danger"></i>
                        ) : (
                          <i className="bi bi-circle text-muted"></i>
                        )}
                      </span>
                      <div className="flex-grow-1">
                        <span className={item.complete ? 'text-success' : item.required ? 'fw-medium' : 'text-muted'}>
                          {item.label}
                          {item.required && !item.complete && <span className="text-danger ms-1">*</span>}
                        </span>
                      </div>
                      <i className="bi bi-chevron-right text-muted small"></i>
                    </li>
                  ))}
                </ul>

                {/* Ready to Save Indicator */}
                {newCustomerChecklist.allRequiredComplete && (
                  <div className="p-3 bg-success-subtle text-success text-center">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    <strong>Ready to Save!</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- CONTACT MODAL (Basic CSS Overlay) --- */}
      {showContactModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999
        }}>
          <div className="bg-white p-4 rounded shadow" style={{ width: "400px" }}>
            <h5>{contactId ? "Edit Contact" : "Add New Contact"}</h5>
            <div className="mb-2">
              <label>Name</label>
              <input className="form-control" value={cName} onChange={e => setCName(e.target.value)} />
            </div>
            <div className="mb-2">
              <label>Role</label>
              <select className="form-select" value={cRole} onChange={e => setCRole(e.target.value)}>
                <option value="General">General</option>
                <option value="Billing">Billing</option>
                <option value="Shipping">Shipping</option>
                <option value="Purchasing">Purchasing</option>
                <option value="Quotes">Quotes</option>
              </select>
            </div>
            <div className="mb-2">
              <label>Email</label>
              <input className="form-control" value={cEmail} onChange={e => setCEmail(e.target.value)} />
            </div>
            <div className="mb-3">
              <label>Phone</label>
              <input className="form-control" value={cPhone} onChange={e => setCPhone(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" onClick={() => setShowContactModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveContact}>Save Contact</button>
            </div>
          </div>
        </div>
      )}

      {/* --- SHIP-TO ADDRESS MODAL --- */}
      {showAddressModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999
        }}>
          <div className="bg-white p-4 rounded shadow" style={{ width: "480px", maxHeight: "90vh", overflowY: "auto" }}>
            <h5>{addressId ? "Edit Ship-To Address" : "Add Ship-To Address"}</h5>
            <div className="mb-2">
              <label>Label <span className="text-danger">*</span></label>
              <input className="form-control" value={aLabel} onChange={e => setALabel(e.target.value)} placeholder="e.g. Appleton HQ" />
            </div>
            <div className="mb-2">
              <label>Street Address</label>
              <input className="form-control" value={aAddress} onChange={e => setAAddress(e.target.value)} />
            </div>
            <div className="mb-2">
              <label>Suite / Dock / Line 2</label>
              <input className="form-control" value={aAddress2} onChange={e => setAAddress2(e.target.value)} />
            </div>
            <div className="row g-2 mb-2">
              <div className="col-6"><label>City</label><input className="form-control" value={aCity} onChange={e => setACity(e.target.value)} /></div>
              <div className="col-3"><label>State</label><input className="form-control" value={aState} onChange={e => setAState(e.target.value)} /></div>
              <div className="col-3"><label>Zip</label><input className="form-control" value={aZip} onChange={e => setAZip(e.target.value)} /></div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-6"><label>Receiving Contact</label><input className="form-control" value={aContactName} onChange={e => setAContactName(e.target.value)} /></div>
              <div className="col-6"><label>Contact Phone</label><input className="form-control" value={aContactPhone} onChange={e => setAContactPhone(e.target.value)} /></div>
            </div>
            <div className="mb-2">
              <label>Receiving Hours</label>
              <input className="form-control" value={aReceivingHours} onChange={e => setAReceivingHours(e.target.value)} placeholder="e.g. Mon–Fri 7:00 AM – 3:30 PM" />
              <small className="text-muted">When this location's dock accepts deliveries. Prints on shipping manifests.</small>
            </div>
            <div className="mb-2">
              <label>Delivery Notes</label>
              <textarea className="form-control" rows={2} value={aNotes} onChange={e => setANotes(e.target.value)} placeholder="Gate code, call before delivery, special access…" />
            </div>
            <div className="form-check mb-3">
              <input type="checkbox" className="form-check-input" id="addrDefaultCheck" checked={aIsDefault} onChange={e => setAIsDefault(e.target.checked)} />
              <label className="form-check-label" htmlFor="addrDefaultCheck">Default ship-to (pre-selected on new quotes &amp; orders)</label>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" onClick={() => setShowAddressModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveAddress}>Save Address</button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}